import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Resource, UIComponent } from '../../types';

interface FlashcardSetComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

export const FlashcardSetComponent: React.FC<FlashcardSetComponentProps> = ({ resource, onAction }) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const mergedResource = { ...resource, ...resource.config };

  // Support both legacy structure and new config structure
  const getConfigValue = (key: string, defaultValue: any) => {
    // Check if resource has a config object (new structure)
    if (mergedResource && typeof mergedResource === 'object') {
      return mergedResource[key] !== undefined ? mergedResource[key] : defaultValue;
    }
    // Fall back to legacy structure (direct properties on resource)
    return defaultValue;
  };

  // Get cards/items from either config.cards or resource.items
  const getCards = () => {
    if (mergedResource) {
      return mergedResource.cards || mergedResource.items || [];
    }
    return [];
  };

  const cards = getCards();
  // autoFlip is available but not yet implemented in the UI
  // const autoFlip = getConfigValue('autoFlip', false);
  const showProgress = getConfigValue('showProgress', true);
  const autoAdvanceSeconds = getConfigValue('auto_advance_seconds', 3);

  const currentCard = cards[currentIndex];
  
  // Handle both old format (simple numbers) and new format (objects with front/back)
  const getFrontContent = () => {
    if (typeof currentCard === 'object' && currentCard.front) {
      return currentCard.front;
    }
    return currentCard;
  };
  
  const getBackContent = () => {
    if (typeof currentCard === 'object' && currentCard.back) {
      return currentCard.back;
    }
    return typeof currentCard === 'number' ? `Number ${currentCard}` : currentCard;
  };

  const getAnswerContent = () => {
    if (typeof currentCard === 'object' && currentCard.answer) {
      return currentCard.answer;
    }
    return getBackContent();
  };

  // Reset component state when resource changes (for quiz navigation)
  useEffect(() => {
    setCurrentIndex(0);
    setIsFlipped(false);
    setIsAutoAdvancing(false);
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [resource.id]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const handleNext = () => {
    setIsFlipped(false);
    setCurrentIndex((prev) => (prev + 1) % cards.length);
    onAction?.('next_card', { index: currentIndex, card: currentCard });
  };

  const handlePrevious = () => {
    setIsFlipped(false);
    setCurrentIndex((prev) => (prev - 1 + cards.length) % cards.length);
    onAction?.('previous_card', { index: currentIndex, card: currentCard });
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
    onAction?.('flip_card', { index: currentIndex, card: currentCard, flipped: !isFlipped });
  };

  const handleAutoAdvance = () => {
    if (isAutoAdvancing) {
      // Stop auto advance
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsAutoAdvancing(false);
    } else {
      // Start auto advance
      setIsAutoAdvancing(true);
      intervalRef.current = setInterval(() => {
        handleNext();
      }, autoAdvanceSeconds * 1000);
    }
  };

  if (cards.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-900 mb-2">{t('flashcardSet.flashcardSet')}</h3>
        <p className="text-sm text-gray-600">{t('flashcardSet.noItemsConfigured')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 max-w-md mx-auto">
      <div className="text-center mb-4">
        <h3 className="font-semibold text-gray-900 mb-2 text-lg">{t('flashcardSet.flashcardSet')}</h3>
        {showProgress && (
          <p className="text-sm text-gray-600">
            {t('flashcardSet.card')} {currentIndex + 1} {t('flashcardSet.of')} {cards.length}
          </p>
        )}
      </div>

      <div className="relative h-64 mb-4 perspective-1000">
        <div 
          className={`absolute w-full h-full transition-transform duration-500 transform-style-preserve-3d ${
            isFlipped ? 'rotate-y-180' : ''
          }`}
          style={{
            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            transformStyle: 'preserve-3d'
          }}
        >
          {/* Front of card */}
          <div 
            className="absolute w-full h-full bg-blue-50 rounded-lg p-4 border-2 border-blue-200 backface-hidden overflow-hidden"
            style={{ backfaceVisibility: 'hidden' }}
          >
            <div className="text-center h-full flex flex-col justify-center">
              <div className="text-lg sm:text-xl md:text-2xl font-bold text-blue-800 mb-2 break-words leading-tight">
                {getFrontContent()}
              </div>
              <p className="text-xs sm:text-sm text-blue-600 mt-2">{t('resourceConfig.front')}</p>
            </div>
          </div>
          
          {/* Back of card */}
          <div 
            className="absolute w-full h-full bg-green-50 rounded-lg p-4 border-2 border-green-200 backface-hidden overflow-hidden"
            style={{ 
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)'
            }}
          >
            <div className="text-center h-full flex flex-col justify-center">
              <div className="text-base sm:text-lg md:text-xl font-bold text-green-800 mb-2 break-words leading-tight">
                {getBackContent()}
              </div>
              <p className="text-xs sm:text-sm text-green-600 mt-2">{t('resourceConfig.back')}</p>
              {/* Show answer if available */}
              {typeof currentCard === 'object' && currentCard.answer && (
                <div className="mt-2 p-2 bg-yellow-50 rounded border border-yellow-200 max-h-20 overflow-y-auto">
                  <p className="text-xs font-medium text-yellow-800 mb-1">{t('resourceConfig.answer')}:</p>
                  <p className="text-sm font-bold text-yellow-900 break-words leading-tight">
                    {getAnswerContent()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center space-x-2 mb-3">
        <button
          onClick={handlePrevious}
          className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
        >
          {t('flashcardSet.previous')}
        </button>
        <button
          onClick={handleFlip}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
        >
          {t('flashcardSet.flip')}
        </button>
        <button
          onClick={handleNext}
          className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
        >
          {t('flashcardSet.next')}
        </button>
      </div>

      <div className="text-center">
        <button
          onClick={handleAutoAdvance}
          className={`px-3 py-1 text-xs rounded-lg transition-colors ${
            isAutoAdvancing
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-green-600 text-white hover:bg-green-700'
          }`}
        >
          {isAutoAdvancing ? t('flashcardSet.stopAutoAdvance') : t('flashcardSet.autoAdvance')}
        </button>
      </div>
    </div>
  );
};
